<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Dinero, { Currency } from 'dinero.js';
import { sumDinero } from '@/utils/dinero';
import { BanknotesIcon, CurrencyEuroIcon } from '@heroicons/vue/24/solid';

import type API from '@/services/api';
import type  { RetrieveClaimWithRelationsResponse } from '@/interfaces/data-contracts';

const props = defineProps<{
  claim: RetrieveClaimWithRelationsResponse;
}>();
const emits = defineEmits(["update"]);

const claim = computed(() => props.claim);
const ec261CompensationAmount = computed(() => claim.value.legalSpecification.ec261CompensationAmount)

/** @TODO Computation on API's side with currency exchange, at the rate of the Flight */

const passengersDetails = computed(() => {
  return claim.value?.usersClaims.map(({user, userSegment, montrealConventionCompensationAmount, expenses}) => {
    const isCodeshare = Boolean(claim.value.legalSpecification.effectiveFlightId);
    const dineroExpenses = expenses.map(({expense}) => Dinero({ amount: Number.parseInt(expense.price), precision: 2, currency: expense.currencyCode as Currency }));
    const totalExpenses = sumDinero(dineroExpenses);
    const montrealConvention = Dinero({ amount: Number.parseInt(montrealConventionCompensationAmount || '0'), precision: 0, currency: "EUR" });

    return {
      fullName: `${user.firstName} ${user.lastName.toUpperCase()}`,
      birthDate: user.birthdate,
      codeshare: false,//isCodeshare ? userSegment.segment?.flight.flightIdentifier : null,
      montrealConventionCompensationAmount: montrealConvention,
      pnr: userSegment.segment?.reservation?.gdsPnrId,
      expenses: dineroExpenses,
      totalExpenses, // TODO: Currency conversion
      totalAfterCommission: totalExpenses
        .add(montrealConvention)
        .add(Dinero({ amount: claim.value.legalSpecification.ec261CompensationAmount, precision: 0, currency: "EUR" }))
        .percentage(100 -
          (claim.value.legalSpecification.b2cCommissionPercentageForUpclaim
           + claim.value.legalSpecification.b2cCommissionPercentageForTravelAgency
          )
        )
    }
  });
});

const companyExpenses = computed(() => {
  const companyName = claim.value.legalSpecification.impactedSegment.reservation.travelAgencyCompany?.displayName;
  if (!companyName) {
    return {
      hasExpenses: false,
      totalAfterCommission: Dinero({ amount: 0, precision: 2, currency: "EUR" }),
      totalExpenses: Dinero({ amount: 0, precision: 2, currency: "EUR" }),
      dineroExpenses: Dinero({ amount: 0, precision: 2, currency: "EUR" }),
    };
  }
  const companyExpenses = claim.value.claimExpenses.filter(({ paidBy }) => paidBy === "BUSINESS_TRAVEL_COMPANY");
  const dineroExpenses = companyExpenses.map(({expense}) => Dinero({ amount: Number.parseInt(expense.price), precision: 2, currency: expense.currencyCode as Currency }));
  const totalExpenses = sumDinero(dineroExpenses);
  return {
    name: companyName,
    dineroExpenses,
    totalExpenses,
    totalAfterCommission: totalExpenses.percentage(100 -
      (claim.value.legalSpecification.b2bCommissionPercentageForUpclaim
        + claim.value.legalSpecification.b2bCommissionPercentageForTravelAgency
      )
    ),
    hasExpenses: Boolean(companyExpenses.length > 0)
  }
});

const travelAgencyExpenses = computed(() => {
  const travelAgencyExpenses = claim.value.claimExpenses.filter(({ paidBy }) => paidBy === "TRAVEL_AGENCY");
  const dineroExpenses = travelAgencyExpenses.map(({expense}) => Dinero({ amount: Number.parseInt(expense.price), precision: 2, currency: expense.currencyCode as Currency }));
  const totalExpenses = sumDinero(dineroExpenses);
  return {
    dineroExpenses,
    totalExpenses,
    totalAfterCommission: totalExpenses.percentage(100 -
      claim.value.legalSpecification.travelAgencyLossCommissionPercentageForUpclaim
    ),
    hasExpenses: Boolean(travelAgencyExpenses.length > 0)
  }
});

const total = computed(() => {
  return {
    total: sumDinero([
      ...passengersDetails.value.map(({ totalExpenses }) => totalExpenses),
      ...passengersDetails.value.map(({ montrealConventionCompensationAmount }) => montrealConventionCompensationAmount),
      Dinero({ amount: ec261CompensationAmount.value, currency: "EUR", precision: 0 }).multiply(claim.value.usersClaims.length),
      companyExpenses.value.totalExpenses,
      travelAgencyExpenses.value.totalExpenses
    ]),
    totalAfterCommission: sumDinero([
      ...passengersDetails.value.map(({ totalAfterCommission }) => totalAfterCommission),
      companyExpenses.value.totalAfterCommission,
      travelAgencyExpenses.value.totalAfterCommission
    ]),
  }
});
</script>
<template>
  <div class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
    <dt class="text-sm font-medium leading-6 text-gray-700">
      <span class="inline-flex">
        <BanknotesIcon class="w-5 h-5 mr-2 text-gray-400" />
        Demandes
      </span>
    </dt>
    <dd class="mt-1 text-sm text-gray-700 sm:mt-0 sm:col-span-2">
      <ul v-for="item in passengersDetails" v-tooltip.left="`Après commission : ${item.totalAfterCommission.toFormat('0.00')} €`" class="list-disc">
        <li class="mb-2">
          {{ item.fullName }}
          <ul class="list-disc list-inside">
            <li>
              <span class="font-bold">{{ec261CompensationAmount}}€</span> de Règlement CE261/2004
            </li>
            <li v-if="item.montrealConventionCompensationAmount && !item.montrealConventionCompensationAmount.isZero()">
              <span class="font-bold">{{item.montrealConventionCompensationAmount.toFormat('0.00')}}€</span> de Convention de Montréal 1999
            </li>
            <li v-if="item.expenses?.length">
              <span class="font-bold">{{item.totalExpenses.toFormat('0.00')}}€</span> de frais supplémentaires
            </li>
            <li v-if="item.birthDate">Né(e) le <span class="font-bold">{{ new Date(item.birthDate).toLocaleDateString() }}</span></li>
          </ul> 
        </li>
      </ul>
      <ul v-if="companyExpenses.hasExpenses" class="mt-6 list-disc" v-tooltip.left="`Après commission : ${companyExpenses.totalAfterCommission.toFormat('0.00')} €`">
        <li>
          <span class="inline-flex align-middle">
            {{ companyExpenses.name }} (Entreprise)
            <!-- <span class="ml-2"><Building2 :size="16" /></span> -->
          </span>
          <ul class="list-disc list-inside">
            <li><span class="font-bold">{{ companyExpenses.totalExpenses.toFormat('0.00') }}€</span> de frais supplémentaires</li>
          </ul>
        </li>
      </ul>
      <ul v-if="travelAgencyExpenses.hasExpenses" class="mt-6 list-disc" v-tooltip.left="`Après commission : ${travelAgencyExpenses.totalAfterCommission.toFormat('0.00')} €`">
        <li>
          <span class="inline-flex align-middle">
            Pour l'Agence de voyages
            <!-- <span class="ml-2"><Building2 :size="16" /></span> -->
          </span>
          <ul class="list-disc list-inside">
            <li><span class="font-bold">{{ travelAgencyExpenses.totalExpenses.toFormat('0.00') }}€</span> de demandes</li>
          </ul>
        </li>
      </ul>
    </dd>
      <dt class="pt-5 text-sm font-medium leading-6 text-gray-700 border-t border-dashed">
        <span class="inline-flex">
          <CurrencyEuroIcon class="w-5 h-5 mr-2 text-gray-400" />
          Montant total
        </span>
      </dt>
      <dd class="pt-5 mt-1 text-sm text-gray-900 border-t border-dashed sm:mt-0 sm:col-span-2">
        {{ total.total.toFormat('0.00') }}€
        <span>
          (Après commission:
            <span class="font-semibold">
              {{ total.totalAfterCommission.toFormat('0.00') }}€
            </span>
          )
        </span>
      </dd>
  </div>
</template>